import { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Confirm from "../../../../../components/Confirm";
import { DocumentoEquipamiento, TipoDocumentoOpcion } from "../../../../../types/DocumentoEquipamiento";
import { aplicarCambioCheck, FormDocumentoErrors, validarDocumento } from "../../validaciones";

type ModalTipoDocumentoProps = {
    isOpen: boolean;
    // Documento a editar; nulo cuando se agrega uno nuevo.
    documentoInicial: DocumentoEquipamiento | null;
    // Tipos activos que todavía no están en la grilla (más el del documento que se edita).
    tiposDisponibles: TipoDocumentoOpcion[];
    onAceptar: (documento: DocumentoEquipamiento) => void;
    onCerrar: () => void;
}

export const documentoVacio: DocumentoEquipamiento = {
    tipoDocumentoId: null,
    tipoDocumentoNombre: '',
    obligatorio: false,
    renovable: false,
    fechaEditable: false,
    periodo: 0,
}

const ModalTipoDocumento = ({ isOpen, documentoInicial, tiposDisponibles, onAceptar, onCerrar }: ModalTipoDocumentoProps) => {
    const inicial = documentoInicial ?? documentoVacio;
    const [documento, setDocumento] = useState<DocumentoEquipamiento>(inicial);
    const [errores, setErrores] = useState<FormDocumentoErrors>({});
    const [confirmarSalida, setConfirmarSalida] = useState<boolean>(false);
    const editando = documentoInicial !== null;

    useEffect(() => {
        if (isOpen) {
            setDocumento(documentoInicial ?? documentoVacio);
            setErrores({});
            setConfirmarSalida(false);
        }
    }, [isOpen, documentoInicial]);

    const hayCambiosSinConfirmar = JSON.stringify(documento) !== JSON.stringify(inicial);

    const handleTipo = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value === '' ? null : Number(e.target.value);
        const opcion = tiposDisponibles.find((t) => t.tipoDocumentoId === id);
        setDocumento({ ...documento, tipoDocumentoId: id, tipoDocumentoNombre: opcion?.tipoDocumentoNombre ?? '' });
    }

    const handleCheck = (campo: 'obligatorio' | 'renovable' | 'fechaEditable') => (e: React.ChangeEvent<HTMLInputElement>) => {
        setDocumento(aplicarCambioCheck(documento, campo, e.target.checked));
    }

    const handlePeriodo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const texto = e.target.value.replace(/\D/g, '');
        setDocumento({ ...documento, periodo: texto === '' ? 0 : Number(texto) });
    }

    const handleAceptar = () => {
        const nuevosErrores = validarDocumento(documento);
        setErrores(nuevosErrores);
        if (Object.keys(nuevosErrores).length === 0) {
            onAceptar(documento);
        }
    }

    // Al salir con datos sin confirmar se pregunta si se descartan; los ya confirmados en la grilla no se tocan.
    const handleSalir = () => {
        if (hayCambiosSinConfirmar) {
            setConfirmarSalida(true);
        } else {
            onCerrar();
        }
    }

    // El Confirm no se cierra solo al presionar "Si": hay que cerrarlo junto con el modal
    const descartarYSalir = () => {
        setConfirmarSalida(false);
        onCerrar();
    }

    return (
        <>
            <Modal isOpen={isOpen} toggle={handleSalir} size="md">
                <ModalHeader toggle={handleSalir}>
                    {editando ? 'Editar Tipo de Documento' : 'Agregar Tipo de Documento'}
                </ModalHeader>
                <ModalBody>
                    <div className="row mt-2">
                        <label htmlFor="tipoDocumentoId" className="col-form-label col-4 text-end" style={{ color: 'grey' }}>Tipo de Documento</label>
                        <div className="col-6">
                            <select
                                id="tipoDocumentoId"
                                name="tipoDocumentoId"
                                className={`form-control select-list ${errores.tipoDocumento ? 'is-invalid' : ''}`}
                                value={documento.tipoDocumentoId ?? ''}
                                onChange={handleTipo}
                                disabled={editando}
                            >
                                <option value="">Seleccionar</option>
                                {tiposDisponibles.map((t) => (
                                    <option key={t.tipoDocumentoId} value={t.tipoDocumentoId}>{t.tipoDocumentoNombre}</option>
                                ))}
                            </select>
                            {errores.tipoDocumento && <div className="invalid-feedback">{errores.tipoDocumento}</div>}
                        </div>
                    </div>

                    <div className="row mt-2 align-items-center">
                        <label htmlFor="obligatorio" className="col-form-label col-4 text-end" style={{ color: 'grey' }}>Obligatorio</label>
                        <div className="col-4">
                            <input id="obligatorio" name="obligatorio" type="checkbox" style={{ appearance: 'auto' }}
                                className="form-check-input" checked={documento.obligatorio} onChange={handleCheck('obligatorio')} />
                        </div>
                    </div>

                    <div className="row mt-2 align-items-center">
                        <label htmlFor="renovable" className="col-form-label col-4 text-end" style={{ color: 'grey' }}>Renovable</label>
                        <div className="col-4">
                            <input id="renovable" name="renovable" type="checkbox" style={{ appearance: 'auto' }}
                                className="form-check-input" checked={documento.renovable} onChange={handleCheck('renovable')} />
                        </div>
                    </div>

                    <div className="row mt-2 align-items-center">
                        <label htmlFor="fechaEditable" className="col-form-label col-4 text-end" style={{ color: 'grey' }}>Fecha Editable</label>
                        <div className="col-4">
                            <input id="fechaEditable" name="fechaEditable" type="checkbox" style={{ appearance: 'auto' }}
                                className="form-check-input" checked={documento.fechaEditable}
                                disabled={!documento.renovable} onChange={handleCheck('fechaEditable')} />
                        </div>
                    </div>

                    <div className="row mt-2">
                        <label htmlFor="periodo" className="col-form-label col-4 text-end" style={{ color: 'grey' }}>Periodicidad</label>
                        <div className="col-6">
                            <div className="input-group">
                                <input id="periodo" name="periodo" type="text" inputMode="numeric" maxLength={5}
                                    className={`form-control ${errores.periodo ? 'is-invalid' : ''}`}
                                    value={documento.periodo ? documento.periodo : ''}
                                    disabled={!documento.renovable || documento.fechaEditable}
                                    onChange={handlePeriodo} />
                                <span className="form-control-plaintext ms-1" style={{ width: '75px' }}>Días</span>
                                {errores.periodo && <div className="invalid-feedback">{errores.periodo}</div>}
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleAceptar}>Aceptar</Button>
                    <Button color="secondary" onClick={handleSalir}>Cancelar</Button>
                </ModalFooter>
            </Modal>

            <Confirm isOpen={confirmarSalida} toggle={() => setConfirmarSalida(false)} title='Salir' yesFn={descartarYSalir}>
                <div className="form-horizontal">
                    <div className="row">
                        <p>Hay datos sin agregar. Desea salir sin guardar los cambios?</p>
                    </div>
                </div>
            </Confirm>
        </>
    )
}

export default ModalTipoDocumento;
